import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ValidationErrorService } from 'src/app/core/services/error/validation-error/validation-error.service';
import { FormControlStatus } from 'src/app/shared/models/constants/form-control-status';

@Component({
  selector: 'app-code-input',
  templateUrl: './code-input.component.html',
  styleUrls: ['./code-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Code input atom
 */
export class CodeInputComponent implements OnInit, OnDestroy {
  @Input() hint: string = '';
  @Input() type: string = '';
  @Input() control!: FormControl;
  @Input() validated = false;
  @Input() hasCloseButton = false;
  @Input() subscript = '';
  @Output() closeButtonClick = new EventEmitter<any>();
  @ViewChild('inputField') inputField?: ElementRef<HTMLInputElement>;

  private _readonly: boolean = false;
  private _points: boolean = false;
  errorMessage = '';
  private subsink = new Subscription();
  maxLength!: number;

  /**
   * @constructor
   */
  constructor(private validationErrorService: ValidationErrorService) {}

  /**
   * on init
   */
  ngOnInit() {
    this.setErrorMessage(this.control);
    const subscription = this.control.statusChanges.subscribe((status) => {
      this.setErrorMessage(this.control);
      if (status === FormControlStatus.VALID) {
        this.inputField?.nativeElement.blur();
      }
    });
    this.subsink.add(subscription);
    this.maxLength = this.validationErrorService.getMaximumLength(this.control);
  }

  /**
   * clean up subscriptions
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Whether this input is read-only or not
   */
  @Input()
  get readonly() {
    return this._readonly;
  }

  /**
   * Sets this input as read-only
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set readonly(value: any) {
    this._readonly = coerceBooleanProperty(value);
  }

  /**
   * Whether this input has a hint attached
   * @return {boolean}
   */
  hasHint(): boolean {
    return this.hint?.length > 0;
  }

  /**
   * Sets error message of this input
   * @param {AbstractControl} abstractControl
   */
  setErrorMessage(abstractControl: AbstractControl) {
    this.validationErrorService
      .translateError(abstractControl, 'code')
      .then((errorMessage) => {
        this.errorMessage = errorMessage;
      });
  }

  /**
   * Whether this input is for points codes or not
   */
  @Input()
  get points() {
    return this._points;
  }

  /**
   * Sets this input as point code
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set points(value: any) {
    this._points = coerceBooleanProperty(value);
  }

  /**
   * Emits close button click event for underlying material button
   */
  emitClick() {
    this.closeButtonClick.emit();
  }
}
